import { ContentError } from './errors'
import { fetchWithTimeout, retryAfterOf } from './net'
import type { ContentSource, SourceLoadOptions, SourceResult } from './types'
import { parseBundle } from './validate'

/**
 * 同一份 dist/content.json 通过普通 HTTP 提供时的内容源。
 * 用于本地开发（Vite 代理 /content）或把内容镜像到 CDN 的场景。
 *
 * 它不像 Gitee 那样能先拿 sha；29 KB 的包直接下载再比 contentHash 足够便宜。
 */
export class HttpContentSource implements ContentSource {
  readonly key: string
  readonly label: string

  readonly #url: string

  constructor(url: string) {
    this.#url = new URL(url, document.baseURI).href
    this.key = `http:${this.#url}`
    this.label = `HTTP · ${new URL(this.#url).host}`
  }

  async load(options: SourceLoadOptions): Promise<SourceResult> {
    const response = await fetchWithTimeout(this.#url, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })

    if (response.status === 404) {
      throw new ContentError('not-found', `找不到 ${this.#url}（404）。`)
    }
    if (response.status === 403 || response.status === 429) {
      throw new ContentError('rate-limit', `内容地址被限流（${response.status}）。`, {
        status: response.status,
        retryAfter: retryAfterOf(response),
      })
    }
    if (!response.ok) {
      throw new ContentError('http', `内容地址返回 ${response.status}。`, { status: response.status })
    }

    let raw: unknown
    try {
      raw = await response.json()
    } catch {
      throw new ContentError('parse', `${this.#url} 不是合法 JSON。`)
    }

    const bundle = parseBundle(raw)
    const sha = bundle.contentHash

    if (!options.force && options.cached?.sha === sha) {
      return { status: 'not-modified', sha }
    }

    return { status: 'updated', sha, bundle }
  }
}
